import React, { useEffect, useState } from "react";
import { useLazyQuery } from "@apollo/client";
import { useParams } from "react-router-dom";
import { lang } from "../../../middleware/lang";
import { LoaderDots } from "../../../middleware/loaders";
import { HeaderTitle } from "../common/titles";
import { GET_INVENTORY_MOVEMENTS } from "./queries";
import { InventoryMovement, MOVEMENT_TYPE_LABELS } from "./models";
import InventoryNav from "./inventoryNav";

type Params = {
  ingredient_id?: string;
};

const MOVEMENT_TYPE_OPTIONS = [
  { value: "", label: "Todos" },
  { value: "sale", label: "Venta" },
  { value: "purchase", label: "Compra" },
  { value: "waste", label: "Merma" },
  { value: "adjustment", label: "Ajuste" },
  { value: "transfer_in", label: "Entrada Transfer." },
  { value: "transfer_out", label: "Salida Transfer." },
  { value: "production", label: "Producción" },
  { value: "return", label: "Devolución" },
];

const MovementHistory: React.FC = () => {
  const { ingredient_id } = useParams<Params>();
  const [movementType, setMovementType] = useState("");
  const [page, setPage] = useState(0);
  const PAGE_SIZE = 50;

  const [fetchMovements, { data, loading }] = useLazyQuery(
    GET_INVENTORY_MOVEMENTS,
    { fetchPolicy: "no-cache" }
  );

  useEffect(() => {
    const variables: any = {
      skip: page * PAGE_SIZE,
      take: PAGE_SIZE,
    };
    if (ingredient_id) {
      variables.ingredient_id = parseInt(ingredient_id);
    }
    if (movementType) {
      variables.movement_type = movementType;
    }
    fetchMovements({ variables });
    //eslint-disable-next-line
  }, [ingredient_id, movementType, page]);

  const formatDate = (timestamp: string) => {
    try {
      const date = new Date(parseInt(timestamp));
      return date.toLocaleString("es-MX", {
        day: "2-digit",
        month: "short",
        year: "numeric",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return timestamp;
    }
  };

  return (
    <>
      <HeaderTitle title={lang("Movimientos de Inventario", "Inventory Movements")} />
      <InventoryNav active="movements" />

      <div className="row mb-3">
        <div className="col-lg-3">
          <select
            className="custom-select"
            value={movementType}
            onChange={(e) => {
              setMovementType(e.target.value);
              setPage(0);
            }}
          >
            {MOVEMENT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {ingredient_id && (
          <div className="col-lg-3">
            <span className="badge badge-info p-2">
              {lang("Filtrado por ingrediente", "Filtered by ingredient")} #
              {ingredient_id}
            </span>
          </div>
        )}
      </div>

      {loading && <LoaderDots fullSize />}

      {data && (
        <div className="row">
          <div className="col-lg-12">
            <div className="material_card p-3 table-responsive">
              <table className="table table-hover table-sm">
                <thead className="thead-light">
                  <tr>
                    <th>{lang("Fecha", "Date")}</th>
                    <th>{lang("Ingrediente", "Ingredient")}</th>
                    <th>{lang("Cambio", "Change")}</th>
                    <th>{lang("Tipo", "Type")}</th>
                    <th>{lang("Referencia", "Reference")}</th>
                    <th>{lang("Ubicación", "Location")}</th>
                    <th>{lang("Costo/U", "Cost/U")}</th>
                    <th>{lang("Sucursal", "Branch")}</th>
                    <th>{lang("Notas", "Notes")}</th>
                  </tr>
                </thead>
                <tbody>
                  {data.getInventoryMovements.map(
                    (mov: InventoryMovement) => {
                      const typeInfo =
                        MOVEMENT_TYPE_LABELS[mov.movement_type] || {
                          label: mov.movement_type,
                          color: "secondary",
                        };
                      const isPositive = mov.quantity_change >= 0;
                      return (
                        <tr key={mov.id}>
                          <td>
                            <small>{formatDate(mov.created_at)}</small>
                          </td>
                          <td>
                            {mov.ingredient?.name || `#${mov.ingredient_id}`}
                            <br />
                            <small className="text-muted">
                              {mov.ingredient?.unit}
                            </small>
                          </td>
                          <td
                            className={`font-weight-bold ${
                              isPositive ? "text-success" : "text-danger"
                            }`}
                          >
                            {isPositive ? "+" : ""}
                            {Number(mov.quantity_change).toFixed(2)}
                          </td>
                          <td>
                            <span
                              className={`badge badge-${typeInfo.color}`}
                            >
                              {typeInfo.label}
                            </span>
                          </td>
                          <td>
                            <small>
                              {mov.reference_type}
                              {mov.reference_id
                                ? ` #${mov.reference_id}`
                                : ""}
                            </small>
                          </td>
                          <td>
                            <small>{mov.location || "—"}</small>
                          </td>
                          <td>
                            {mov.cost_per_unit
                              ? `$${Number(mov.cost_per_unit).toFixed(2)}`
                              : "—"}
                          </td>
                          <td>
                            <small>{mov.branch?.name || "—"}</small>
                          </td>
                          <td>
                            <small className="text-muted">
                              {mov.notes || "—"}
                            </small>
                          </td>
                        </tr>
                      );
                    }
                  )}
                  {data.getInventoryMovements.length === 0 && (
                    <tr>
                      <td colSpan={9} className="text-center text-muted py-4">
                        {lang("Sin movimientos", "No movements")}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="d-flex justify-content-between mt-3">
              <button
                className="btn btn-outline-primary btn-sm"
                disabled={page === 0}
                onClick={() => setPage(page - 1)}
              >
                <i className="fa fa-chevron-left"></i>{" "}
                {lang("Anterior", "Previous")}
              </button>
              <span className="text-muted">
                {lang("Página", "Page")} {page + 1}
              </span>
              <button
                className="btn btn-outline-primary btn-sm"
                disabled={
                  data.getInventoryMovements.length < PAGE_SIZE
                }
                onClick={() => setPage(page + 1)}
              >
                {lang("Siguiente", "Next")}{" "}
                <i className="fa fa-chevron-right"></i>
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MovementHistory;
